<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import type { CommandPaletteGroup, CommandPaletteItem } from "@nuxt/ui"
import { defineShortcuts } from "@nuxt/ui/composables"
import { refDebounced } from "@vueuse/core"
import { computed, ref, watch } from "vue"

import { isAssetCode } from "@/lib/assetCode"
import { getJson } from "@/lib/fetch"
import { ICON } from "@/lib/icons"
import {
  rootPath,
  itemsPath,
  containersPath,
  tagsPath,
  itemTypesPath,
  peoplePath,
  loansPath,
  outingsPath,
  capturePath,
  printPath,
  searchPath,
  itemPath,
  containerPath,
  personPath,
  tagPath,
  itemTypePath,
  lookupPath,
} from "@/routes"

interface SearchResult {
  id: string
  label: string
  suffix?: string
  avatar?: string
  icon?: string
  archived_at?: string | null
}

interface SearchResponse {
  items?: SearchResult[]
  containers?: SearchResult[]
  people?: SearchResult[]
  tags?: SearchResult[]
  item_types?: SearchResult[]
}

interface LookupResponse {
  id: string
  name: string
  is_container: boolean
  archived_at: string | null
}

type Group = CommandPaletteGroup<CommandPaletteItem>

const open = ref(false)
const searchTerm = ref("")
const searchTermDebounced = refDebounced(searchTerm, 300)
const loading = ref(false)
const results = ref<SearchResponse>({})

defineShortcuts({
  meta_k: () => {
    open.value = !open.value
  },
})

function navigate(path: string) {
  open.value = false
  router.visit(path)
}

const navigationGroup: Group = {
  id: "navigation",
  label: "Go to",
  items: [
    { label: "Dashboard", icon: ICON.home, onSelect: () => navigate(rootPath()) },
    { label: "Items", icon: ICON.item, onSelect: () => navigate(itemsPath()) },
    { label: "Containers", icon: ICON.container, onSelect: () => navigate(containersPath()) },
    { label: "Tags", icon: ICON.tag, onSelect: () => navigate(tagsPath()) },
    { label: "Types", icon: ICON.itemType, onSelect: () => navigate(itemTypesPath()) },
    { label: "People", icon: ICON.people, onSelect: () => navigate(peoplePath()) },
    { label: "Loans", icon: ICON.loan, onSelect: () => navigate(loansPath()) },
    { label: "Outings", icon: ICON.outing, onSelect: () => navigate(outingsPath()) },
    { label: "Print", icon: ICON.print, onSelect: () => navigate(printPath()) },
  ],
}

const actionsGroup: Group = {
  id: "actions",
  label: "Actions",
  items: [
    { label: "Start capture", icon: ICON.capture, onSelect: () => navigate(capturePath()) },
    { label: "Scan QR", icon: ICON.scanQr, onSelect: () => navigate(capturePath() + "?mode=scan") },
  ],
}

function resultGroup(
  id: string,
  label: string,
  items: SearchResult[],
  pathFn: (id: string) => string,
): Group | null {
  if (!items?.length) return null
  return {
    id,
    label,
    items: items.map((item) => ({
      ...item,
      avatar: item.avatar ? { src: item.avatar } : undefined,
      onSelect: () => navigate(pathFn(item.id)),
    })),
    ignoreFilter: true,
  }
}

const groups = computed<Group[]>(() => {
  const searchGroups = [
    resultGroup("items", "Items", results.value.items || [], (id) => itemPath(id)),
    resultGroup("containers", "Containers", results.value.containers || [], (id) =>
      containerPath(id),
    ),
    resultGroup("people", "People", results.value.people || [], (id) => personPath(id)),
    resultGroup("tags", "Tags", results.value.tags || [], (id) => tagPath(id)),
    resultGroup("item_types", "Types", results.value.item_types || [], (id) => itemTypePath(id)),
  ].filter((g): g is Group => g !== null)

  return [navigationGroup, actionsGroup, ...searchGroups]
})

let abortController: AbortController | null = null

watch(searchTermDebounced, async (query) => {
  if (!query) {
    results.value = {}
    return
  }

  abortController?.abort()
  abortController = new AbortController()
  loading.value = true

  try {
    if (isAssetCode(query)) {
      const res = await getJson(
        `${lookupPath()}?code=${encodeURIComponent(query)}`,
        abortController.signal,
      )
      if (!res.ok) {
        results.value = {}
        return
      }

      const item = (await res.json()) as LookupResponse
      const result = {
        id: item.id,
        label: item.name,
        suffix: item.archived_at ? "Archived" : undefined,
      }
      results.value = item.is_container ? { containers: [result] } : { items: [result] }
      return
    }

    const res = await getJson(
      `${searchPath()}?q=${encodeURIComponent(query)}`,
      abortController.signal,
    )
    if (res.ok) {
      results.value = await res.json()
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return
  } finally {
    loading.value = false
  }
})

watch(open, (isOpen) => {
  if (!isOpen) {
    searchTerm.value = ""
    results.value = {}
  }
})

defineExpose({ open: () => (open.value = true) })
</script>

<template>
  <UModal v-model:open="open">
    <template #content>
      <UCommandPalette
        v-model:search-term="searchTerm"
        :loading="loading"
        :groups="groups"
        placeholder="Search items, containers, people..."
        class="h-80"
        :close="false"
      />
    </template>
  </UModal>
</template>
